import React from 'react';
import { Reveal, SectionHead, IconTile, Counter } from './UI';
import SplitText from './SplitText';
import BorderBeamPanel from './ui/border-beam-panel';

const CARDS = [
  {
    icon: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" opacity=".35" />
        <line x1="3" y1="3" x2="21" y2="21" />
        <path d="M9.5 9.7A3 3 0 0 0 14.3 14.5" />
      </>
    ),
    title: 'Violations go unnoticed',
    body: 'Missing PPE, skipped steps and unsafe behaviour usually surface in a post-incident review — not in time to stop it.',
    gradient: 'linear-gradient(180deg, #E7E1FA 0%, #F3F0FC 100%)',
    beamColors: ['#8B5CF6', '#a78bfa'],
  },
  {
    icon: (
      <>
        <path d="M12 2l8 4v6c0 5.2-3.5 8.6-8 10-4.5-1.4-8-4.8-8-10V6z" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <circle cx="12" cy="16.2" r=".9" fill="currentColor" stroke="none" />
      </>
    ),
    title: 'Compliance gaps pile up quietly',
    body: "Manual walk-throughs and periodic audits can't keep pace with a live floor, leaving real exposure between checks.",
    gradient: 'linear-gradient(180deg, #E7E1FA 0%, #F3F0FC 100%)',
    beamColors: ['#8B5CF6', '#a78bfa'],
  },
  {
    icon: (
      <>
        <polyline points="3,7 10,14 14,10 21,17" />
        <polyline points="15,17 21,17 21,11" />
      </>
    ),
    title: 'Losses go undetected',
    body: 'Idle machines, unauthorised access and material loss rarely trip an alarm. They just quietly erode the bottom line.',
    gradient: 'linear-gradient(180deg, #E7E1FA 0%, #F3F0FC 100%)',
    beamColors: ['#8B5CF6', '#a78bfa'],
  },
];

const STATS = [
  { count: 16550, prefix: '$', label: 'Max. fine per serious OSHA violation, 2026' },
  { count: 165514, prefix: '$', label: 'Max. fine per willful / repeat violation' },
];

export default function Problem() {
  return (
    <section id="problem" className="section section-white">
      <style>{`
        #problem [class*="eyebrow"]::before,
        #problem [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }
        #problem .problem-container {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(12px, 2vw, 20px);
        }
        #problem .problem-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 26px;
        }
        /* BorderBeamPanel wrapper styles */
        #problem .problem-card {
          position: relative;
          border-radius: 24px;
          border: 2px solid #111111;
          box-shadow: 0 6px 24px rgba(20, 30, 40, 0.06);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        #problem .problem-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(20, 30, 40, 0.1);
        }
        /* BorderBeamPanel inner content layout */
        #problem .bbp-content {
          padding: clamp(28px, 3vw, 40px);
          min-height: 300px;
          justify-content: space-between;
          gap: 24px;
        }
        #problem .problem-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }
        #problem .problem-card-title {
          font-family: var(--font-display);
          font-size: clamp(22px, 2.2vw, 28px);
          font-weight: 400;
          line-height: 1.25;
          margin: 0;
          color: var(--ink);
          max-width: 80%;
        }
        #problem .problem-card-body {
          color: var(--ink-soft);
          font-size: clamp(15px, 1.15vw, 17.5px);
          line-height: 1.7;
          margin: 0;
        }
        #problem .problem-icon-tile {
          flex-shrink: 0;
          width: 52px;
          height: 52px;
        }
        @media (max-width: 900px) {
          #problem .problem-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 620px) {
          #problem .problem-grid {
            grid-template-columns: 1fr;
          }
          #problem .bbp-content {
            min-height: 240px;
          }
        }
      `}</style>

      <div className="problem-container">
        <Reveal>
          <SectionHead
            center
            eyebrow="The Blind Spot"
            h2="Cameras record. They don't respond."
            p="Most industrial sites run dozens of CCTV feeds around the clock. Without someone watching every screen every second, most of that footage only gets reviewed after something has already gone wrong."
          />
        </Reveal>

        {/* Cards */}
        <div className="problem-grid">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <BorderBeamPanel
                className="problem-card"
                beams={2}
                colors={c.beamColors}
                thickness={2}
                radius={24}
                idleSpeed={38}
                hoverSpeed={100}
                glow
                seed={i + 1}
                style={{
                  background: c.gradient,
                  height: '100%',
                }}
              >
                <div className="problem-card-top">
                  <SplitText
                    tag="h3"
                    text={c.title}
                    delay={30}
                    duration={0.8}
                    ease="power3.out"
                    splitType="words"
                    from={{ opacity: 0, y: 20 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0.1}
                    rootMargin="-40px"
                    textAlign="left"
                    className="problem-card-title"
                  />
                  <div className="problem-icon-tile">
                    <IconTile>{c.icon}</IconTile>
                  </div>
                </div>
                <SplitText
                  tag="p"
                  text={c.body}
                  delay={10}
                  duration={0.7}
                  ease="power2.out"
                  splitType="words"
                  from={{ opacity: 0, y: 15 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="problem-card-body"
                />
              </BorderBeamPanel>
            </Reveal>
          ))}
        </div>

        {/* Callout with counters */}
        <Reveal delay={160}>
          <div style={{
            marginTop: 36,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 16,
            background: 'var(--heat-light)',
            border: '2px solid #111111',
            borderRadius: 18,
            padding: '28px 32px',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--heat)" strokeWidth={1.7}
              strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 26, height: 26, flexShrink: 0 }}>
              <path d="M12 3 22 20 H2 Z" />
              <line x1="12" y1="10" x2="12" y2="14.5" />
              <circle cx="12" cy="17.3" r=".9" fill="var(--heat)" stroke="none" />
            </svg>
            <div style={{ width: '100%', maxWidth: 780, margin: '0 auto' }}>
              <SplitText
                tag="p"
                text="Regulatory penalties for safety failures are real money, not an abstraction — before the cost of downtime, legal exposure or lost trust is even counted."
                delay={10}
                duration={0.7}
                ease="power2.out"
                splitType="words"
                from={{ opacity: 0, y: 15 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0.1}
                rootMargin="-40px"
                textAlign="center"
                style={{ color: 'var(--ink-soft)', fontSize: 15, lineHeight: 1.65, marginBottom: 24, textAlign: 'center' }}
              />
              <div style={{ display: 'flex', gap: 40, justifyContent: 'center', flexWrap: 'wrap' }}>
                {STATS.map(s => (
                  <div key={s.label} style={{ textAlign: 'center', flex: '1 1 200px', maxWidth: 300 }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      fontSize: 'clamp(22px, 2.8vw, 32px)',
                      color: 'var(--heat)',
                      display: 'block',
                      marginBottom: 4,
                      textAlign: 'center',
                    }}>
                      <Counter to={s.count} prefix={s.prefix} />
                    </span>
                    <SplitText
                      tag="span"
                      text={s.label}
                      delay={10}
                      duration={0.7}
                      ease="power2.out"
                      splitType="words"
                      from={{ opacity: 0, y: 10 }}
                      to={{ opacity: 1, y: 0 }}
                      threshold={0.1}
                      rootMargin="-40px"
                      textAlign="center"
                      style={{ fontSize: 13, color: 'var(--ink-soft)', textAlign: 'center' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}